import { useEffect, useState } from "react";
import { ArrowLeft, FileDown, Plus, Trash2, X } from "lucide-react";
import {
  useCreate,
  useDelete,
  useGetList,
  useGetOne,
  useLocaleState,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { formatMoney } from "../clients/clientModel";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type {
  Company,
  Deal,
  Proposal,
  ProposalItem,
  ProposalStatus,
} from "../types";
import {
  PROPOSAL_STATUSES,
  defaultValidUntil,
  monthlyTotal,
} from "./proposalModel";

const NEW_ID = "new";

export const ProposalEditPage = () => {
  const { id = NEW_ID } = useParams();
  const { data: proposal, isPending } = useGetOne<Proposal>(
    "proposals",
    { id },
    { enabled: id !== NEW_ID },
  );
  if (id !== NEW_ID && (isPending || !proposal)) return null;
  return <ProposalForm key={id} proposal={proposal} />;
};

ProposalEditPage.path = "/proposals/:id";

const ProposalForm = ({ proposal }: { proposal?: Proposal }) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const notify = useNotify();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { dealCategories } = useConfigurationContext();
  const [create, { isPending: isCreating }] = useCreate<Proposal>();
  const [update, { isPending: isUpdating }] = useUpdate<Proposal>();
  const [deleteOne] = useDelete<Proposal>();

  const dealIdParam = searchParams.get("deal");
  const { data: deal } = useGetOne<Deal>(
    "deals",
    { id: dealIdParam ?? 0 },
    { enabled: !proposal && dealIdParam != null },
  );
  const { data: companies = [] } = useGetList<Company>("companies", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "name", order: "ASC" },
  });

  const [companyId, setCompanyId] = useState(
    proposal
      ? String(proposal.company_id)
      : (searchParams.get("company") ?? ""),
  );
  const [title, setTitle] = useState(proposal?.title ?? "");
  const [status, setStatus] = useState<ProposalStatus>(
    proposal?.status ?? "draft",
  );
  const [intro, setIntro] = useState(proposal?.intro ?? "");
  const [items, setItems] = useState<ProposalItem[]>(
    proposal?.items?.length
      ? proposal.items
      : [{ service: "", description: "", monthly_price: 0 }],
  );
  const [setupFee, setSetupFee] = useState(
    proposal?.setup_fee != null ? String(proposal.setup_fee) : "",
  );
  const [validUntil, setValidUntil] = useState(
    proposal?.valid_until ?? defaultValidUntil(),
  );
  const [terms, setTerms] = useState(
    proposal?.terms ?? translate("resources.proposals.default_terms"),
  );
  const [error, setError] = useState<string | null>(null);

  // A new proposal from a deal starts with that deal's company.
  useEffect(() => {
    if (!proposal && deal?.company_id != null && !companyId) {
      setCompanyId(String(deal.company_id));
    }
  }, [deal, proposal, companyId]);

  const companyName =
    companies.find((company) => String(company.id) === companyId)?.name ?? "";

  // Fill title and intro with the company name until the user edits them.
  useEffect(() => {
    if (proposal || !companyName) return;
    setTitle((current) =>
      current
        ? current
        : translate("resources.proposals.default_title", {
            company: companyName,
          }),
    );
    setIntro((current) =>
      current
        ? current
        : translate("resources.proposals.default_intro", {
            company: companyName,
          }),
    );
  }, [companyName, proposal, translate]);

  const updateItem = (index: number, changes: Partial<ProposalItem>) =>
    setItems((current) =>
      current.map((item, i) => (i === index ? { ...item, ...changes } : item)),
    );

  const parseMoney = (value: string) =>
    Number(value.replace(/[^\d]/g, "")) || 0;

  const handleSave = () => {
    const cleanItems = items
      .filter((item) => item.service || item.description || item.monthly_price)
      .map((item) => ({
        service: item.service,
        description: item.description?.trim() ?? "",
        monthly_price: Number(item.monthly_price) || 0,
      }));
    if (!companyId || !title.trim() || cleanItems.length === 0) {
      setError(translate("ra.validation.required"));
      return;
    }
    const values = {
      company_id: Number(companyId),
      deal_id: proposal?.deal_id ?? (dealIdParam ? Number(dealIdParam) : null),
      title: title.trim(),
      status,
      intro: intro.trim() || null,
      items: cleanItems,
      setup_fee: setupFee.trim() ? parseMoney(setupFee) : null,
      valid_until: validUntil || null,
      terms: terms.trim() || null,
      sent_at:
        status === "sent" && !proposal?.sent_at
          ? new Date().toISOString()
          : (proposal?.sent_at ?? null),
    };
    const options = {
      onSuccess: (saved: Proposal) => {
        notify("resources.proposals.saved", { type: "success" });
        if (!proposal) navigate(`/proposals/${saved.id}`, { replace: true });
      },
      onError: () => notify("ra.notification.http_error", { type: "error" }),
    };
    if (proposal) {
      update(
        "proposals",
        { id: proposal.id, data: values, previousData: proposal },
        { ...options, mutationMode: "pessimistic" },
      );
    } else {
      create("proposals", { data: values }, options);
    }
  };

  const handleDelete = () => {
    if (!proposal) return;
    if (!window.confirm(translate("resources.proposals.delete_confirm")))
      return;
    deleteOne(
      "proposals",
      { id: proposal.id, previousData: proposal },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("resources.proposals.deleted", { type: "success" });
          navigate("/proposals");
        },
      },
    );
  };

  const total = monthlyTotal(items);

  return (
    <div className="mt-6 flex flex-col gap-5 pb-8">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/proposals"
          className="flex items-center gap-1.5 text-sm text-muted-foreground no-underline hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          {translate("resources.proposals.back")}
        </Link>
        <h1 className="flex-1 text-3xl font-semibold tracking-tight">
          {translate(
            proposal
              ? "resources.proposals.edit"
              : "resources.proposals.create",
          )}
        </h1>
        {proposal ? (
          <>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() =>
                window.open(`#/proposals/${proposal.id}/print`, "_blank")
              }
            >
              <FileDown className="size-4" />
              {translate("resources.proposals.view_pdf")}
            </Button>
            <Button
              variant="ghost"
              className="rounded-full text-destructive"
              aria-label="delete"
              onClick={handleDelete}
            >
              <Trash2 className="size-4" />
            </Button>
          </>
        ) : null}
        <Button
          className="rounded-full"
          disabled={isCreating || isUpdating}
          onClick={handleSave}
        >
          {translate("ra.action.save")}
        </Button>
      </div>

      <section className="grid gap-4 rounded-[22px] bg-card p-5 md:grid-cols-2">
        <Field label={translate("resources.proposals.fields.company_id")}>
          <Select
            value={companyId}
            onValueChange={(value) => {
              setCompanyId(value);
              setError(null);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {companies.map((company) => (
                <SelectItem key={company.id} value={String(company.id)}>
                  {company.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label={translate("resources.proposals.fields.status")}>
          <Select
            value={status}
            onValueChange={(value) => setStatus(value as ProposalStatus)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROPOSAL_STATUSES.map((option) => (
                <SelectItem key={option} value={option}>
                  {translate(`resources.proposals.statuses.${option}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field
          label={translate("resources.proposals.fields.title")}
          className="md:col-span-2"
        >
          <Input
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              setError(null);
            }}
          />
        </Field>
        <Field
          label={translate("resources.proposals.fields.intro")}
          className="md:col-span-2"
        >
          <Textarea
            rows={3}
            value={intro}
            onChange={(event) => setIntro(event.target.value)}
          />
        </Field>
      </section>

      <section className="flex flex-col gap-3 rounded-[22px] bg-card p-5">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {translate("resources.proposals.fields.items")}
        </h2>
        {items.map((item, index) => (
          <div
            key={index}
            className="grid gap-3 rounded-2xl bg-secondary p-4 md:grid-cols-[220px_1fr_180px_auto]"
          >
            <Field label={translate("resources.proposals.fields.service")}>
              <Select
                value={item.service}
                onValueChange={(value) => updateItem(index, { service: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {dealCategories.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={translate("resources.proposals.fields.description")}>
              <Textarea
                rows={2}
                value={item.description ?? ""}
                onChange={(event) =>
                  updateItem(index, { description: event.target.value })
                }
              />
            </Field>
            <Field
              label={translate("resources.proposals.fields.monthly_price")}
            >
              <Input
                inputMode="numeric"
                value={item.monthly_price ? String(item.monthly_price) : ""}
                placeholder="1500000"
                onChange={(event) =>
                  updateItem(index, {
                    monthly_price: parseMoney(event.target.value),
                  })
                }
              />
            </Field>
            <button
              type="button"
              aria-label="remove"
              onClick={() =>
                setItems((current) => current.filter((_, i) => i !== index))
              }
              className="self-center text-muted-foreground hover:text-destructive"
            >
              <X className="size-4" />
            </button>
          </div>
        ))}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() =>
              setItems((current) => [
                ...current,
                { service: "", description: "", monthly_price: 0 },
              ])
            }
          >
            <Plus className="size-4" />
            {translate("resources.proposals.add_item")}
          </Button>
          <div className="text-right">
            <div className="text-xs text-muted-foreground">
              {translate("resources.proposals.monthly_total")}
            </div>
            <div className="text-3xl font-semibold tracking-tight">
              {formatMoney(total, "COP", locale)}
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 rounded-[22px] bg-card p-5 md:grid-cols-2">
        <Field label={translate("resources.proposals.fields.setup_fee")}>
          <Input
            inputMode="numeric"
            value={setupFee}
            onChange={(event) => setSetupFee(event.target.value)}
          />
        </Field>
        <Field label={translate("resources.proposals.fields.valid_until")}>
          <Input
            type="date"
            value={validUntil}
            onChange={(event) => setValidUntil(event.target.value)}
          />
        </Field>
        <Field
          label={translate("resources.proposals.fields.terms")}
          className="md:col-span-2"
        >
          <Textarea
            rows={3}
            value={terms}
            onChange={(event) => setTerms(event.target.value)}
          />
        </Field>
      </section>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </div>
  );
};

const Field = ({
  label,
  className = "",
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label>{label}</Label>
    {children}
  </div>
);
